import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  getPermissionDeniedMessage,
  isPermissionDenied,
} from "@1823-partners/core";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  DateFilterModule,
  NumberFilterModule,
  QuickFilterModule,
  TextFilterModule,
} from "ag-grid-community";
import {
  ExcelExportModule,
} from "ag-grid-enterprise";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Analytics as AnalyticsIcon
} from "@mui/icons-material";

import api, { getAnalytics } from "../api";

// Stable row id: derive directly from the row data (PamGrid wraps this as
// (params) => getRowId(params.data) internally). Row data keeps null for
// missing values, so a null key part becomes '' rather than the text 'null'.
const getAnalyticsRowId = (data) => `${data.CUSIP ?? ''}_${data.ACCT_ID ?? ''}_${data.LOT_ID ?? ''}`;

const AnalyticsGrid = ({ selectedDate }) => {
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // The date the rows on screen were loaded for, and the load bookkeeping that
  // keeps a superseded response from replacing them.
  const [loadedDate, setLoadedDate] = useState(null);
  const loadGenerationRef = useRef(0);

  const theme = useTheme();
  const gridRef = useRef();

  // Per-user view-options (column order/width/visibility/sort/filter) backed
  // by the shared /grid-configs blueprint. Owning the hook here lets us bridge
  // its onGridReady into PamGrid so a default layout auto-applies once ready.
  const gridConfigs = useGridConfigs(api, {
    app: 'surveillance',
    gridId: 'analytics',
    gridApiRef: gridRef,
  });

  // AG Grid modules
  const gridModules = useMemo(() => [
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    QuickFilterModule,
    CellStyleModule,
    ExcelExportModule
  ], []);

  // Default column definitions
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 80,
    editable: false, // Read-only
    suppressMenu: false,
  }), []);

  // Column definitions for analytics data
  const columnDefs = useMemo(() => [
    // Security and Account Identification
    {
      headerName: "CUSIP",
      headerTooltip: "Committee on Uniform Securities Identification Procedures number",
      field: "CUSIP",
      pinned: 'left',
      minWidth: 110
    },
    {
      headerName: "Account ID",
      headerTooltip: "Account identifier",
      field: "ACCT_ID",
      pinned: 'left',
      minWidth: 120
    },
    {
      headerName: "Lot ID",
      headerTooltip: "Lot identifier",
      field: "LOT_ID",
      pinned: 'left',
      minWidth: 100
    },
    {
      headerName: "Deal",
      headerTooltip: "Deal identifier",
      field: "DEAL",
      minWidth: 100
    },
    {
      headerName: "Tranche",
      headerTooltip: "Tranche identifier",
      field: "TRANCHE",
      minWidth: 100
    },

    // Core Analytics
    {
      headerName: "WAL",
      headerTooltip: "Weighted Average Life",
      field: "WAL",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 80
    },

    // Book Price Metrics
    {
      headerName: "Book Price",
      headerTooltip: "Book price of the security",
      field: "BOOK_PRICE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 110
    },
    {
      headerName: "Book Yield",
      headerTooltip: "Book yield percentage",
      field: "BOOK_YIELD",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        }).format(params.value) + '%';
      },
      minWidth: 110
    },
    {
      headerName: "Book Value",
      headerTooltip: "Book value of the security",
      field: "BOOK_VALUE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 120
    },

    // Market Price Metrics
    {
      headerName: "Market Price",
      headerTooltip: "Market price of the security",
      field: "MARKET_PRICE",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 120
    },
    {
      headerName: "Market Yield",
      headerTooltip: "Market yield percentage",
      field: "MARKET_YIELD",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        }).format(params.value) + '%';
      },
      minWidth: 120
    },

    // Spread Metrics
    {
      headerName: "Spread BP",
      headerTooltip: "Spread in basis points (book price)",
      field: "SPREAD_BP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value) + ' bp';
      },
      minWidth: 100
    },
    {
      headerName: "Spread MP",
      headerTooltip: "Spread in basis points (market price)",
      field: "SPREAD_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value) + ' bp';
      },
      minWidth: 100
    },

    // Discount Margin Metrics
    {
      headerName: "Disc Margin BP",
      headerTooltip: "Discount margin basis points (book price)",
      field: "DISC_MARGIN_BP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value) + ' bp';
      },
      minWidth: 130
    },
    {
      headerName: "Disc Margin MP",
      headerTooltip: "Discount margin basis points (market price)",
      field: "DISC_MARGIN_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(params.value) + ' bp';
      },
      minWidth: 130
    },

    // Duration Metrics
    {
      headerName: "Mod Duration BP",
      headerTooltip: "Modified duration (book price)",
      field: "MOD_DURATION_BP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        }).format(params.value);
      },
      minWidth: 140
    },
    {
      headerName: "Mod Duration MP",
      headerTooltip: "Modified duration (market price)",
      field: "MOD_DURATION_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        }).format(params.value);
      },
      minWidth: 140
    },
    {
      headerName: "Eff Duration BP",
      headerTooltip: "Effective duration (book price)",
      field: "EFF_DURATION_BP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 6,
          maximumFractionDigits: 6
        }).format(params.value);
      },
      minWidth: 140
    },
    {
      headerName: "Eff Duration MP",
      headerTooltip: "Effective duration (market price)",
      field: "EFF_DURATION_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 6,
          maximumFractionDigits: 6
        }).format(params.value);
      },
      minWidth: 140
    },

    // Convexity Metrics
    {
      headerName: "Mod Convexity BP",
      headerTooltip: "Modified convexity (book price)",
      field: "MOD_CONVEXITY_BP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 150
    },
    {
      headerName: "Mod Convexity MP",
      headerTooltip: "Modified convexity (market price)",
      field: "MOD_CONVEXITY_MP",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 150
    },

    // Loss and Risk Metrics
    {
      headerName: "Principal Writedown",
      headerTooltip: "Principal writedown amount",
      field: "PRINCIPAL_WRITEDOWN",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 150
    },
    {
      headerName: "Principal Window",
      headerTooltip: "Principal window value",
      field: "PRINCIPAL_WINDOW",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 130
    },
    {
      headerName: "Cashflow Window",
      headerTooltip: "Cashflow window value",
      field: "CASHFLOW_WINDOW",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 140
    },
    {
      headerName: "Net Asset Loss",
      headerTooltip: "Net asset loss amount",
      field: "NET_ASSET_LOSS",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      cellStyle: params => {
        if (params.value < 0) {
          return { color: '#c62828', fontWeight: 'bold' };
        }
        return {};
      },
      minWidth: 140
    },
    {
      headerName: "Net Asset Loss History",
      headerTooltip: "Historical net asset loss data",
      field: "NET_ASSET_LOSS_HISTORY",
      type: 'numericColumn',
      valueFormatter: params => {
        if (params.value == null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      minWidth: 170
    },

    // Meta Information
    {
      headerName: "Purpose",
      headerTooltip: "Calculation purpose",
      field: "PURPOSE",
      minWidth: 100
    },
    {
      headerName: "Scenario ID",
      headerTooltip: "Scenario identifier",
      field: "SCENID",
      minWidth: 100
    },
    {
      headerName: "Curve Type",
      headerTooltip: "Interest rate curve type",
      field: "CURVE_TYPE",
      minWidth: 110
    },
    {
      headerName: "Index Date",
      headerTooltip: "Index date",
      field: "INDEX_DATE",
      minWidth: 110,
      valueFormatter: params => {
        if (!params.value) return '';
        const dateStr = params.value.toString();
        if (dateStr.length === 8) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return `${month}/${day}/${year}`;
        }
        return params.value;
      }
    },
    {
      headerName: "As Of Date",
      headerTooltip: "As of date",
      field: "ASOFDT",
      minWidth: 120,
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toISOString().split('T')[0];
      }
    },
    {
      headerName: "Modified By",
      headerTooltip: "Modified by user",
      field: "MODIFIED_BY",
      minWidth: 100,
      editable: false
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Last modified date",
      field: "MODIFIED_DATE",
      minWidth: 140,
      editable: false,
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-US');
      }
    },
    {
      headerName: "Run Date",
      headerTooltip: "Run date",
      field: "RUN_DATE",
      minWidth: 140,
      valueFormatter: params => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleString('en-US');
      }
    }
  ], []);

  // Load analytics data. Only the latest load may update the grid: a slow
  // response for a date the user has since left is dropped.
  const loadAnalyticsData = useCallback(async (date) => {
    if (!date) return;

    const generation = ++loadGenerationRef.current;
    const isCurrent = () => generation === loadGenerationRef.current;
    setIsLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await getAnalytics(date);
      if (!isCurrent()) return;

      const data = response.data || [];

      // Format data keys to uppercase for consistency. Missing values stay
      // null: '' would reach the numeric formatters (and the Excel export) as
      // a false 0.
      const formattedData = data.map(item => {
        const formattedItem = {};
        Object.keys(item).forEach(key => {
          const upperKey = key.toUpperCase();
          formattedItem[upperKey] = item[key];
        });
        return formattedItem;
      });

      setRowData(formattedData);
      setLoadedDate(date);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching analytics data:", error);
      if (isPermissionDenied(error)) {
        // Permission denial: surface the guidance and do NOT render fake
        // sample data.
        setError(getPermissionDeniedMessage(error));
      } else {
        setError("Failed to load analytics data. Please try again.");
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, []);

  // Load analytics data when selected date changes. The previous date's rows
  // are cleared at once so they are never shown or exported under the new date.
  useEffect(() => {
    setRowData([]);
    setLoadedDate(null);
    if (selectedDate) {
      loadAnalyticsData(selectedDate);
    } else {
      loadGenerationRef.current += 1;
      setIsLoading(false);
    }
  }, [selectedDate, loadAnalyticsData]);

  // A response that lands after unmount must not touch state.
  useEffect(() => () => {
    loadGenerationRef.current += 1;
  }, []);

  const handleRefresh = useCallback(() => {
    if (selectedDate) {
      loadAnalyticsData(selectedDate);
    }
  }, [selectedDate, loadAnalyticsData]);

  const handleExportToExcel = useCallback(() => {
    // Export only rows loaded for the date the file is named after.
    if (gridRef.current && selectedDate && loadedDate === selectedDate) {
      const dateStr = selectedDate.replace(/:/g, '-').split('T')[0];
      const params = {
        fileName: `analytics_data_${dateStr}.xlsx`,
        sheetName: 'Analytics Data',
        exportMode: 'xlsx',
        skipColumnHeaders: false,
        skipColumnGroupHeaders: false,
        allColumns: false,
        onlySelected: false
      };

      gridRef.current.api.exportDataAsExcel(params);
    }
  }, [selectedDate, loadedDate]);

  const handleEditFilter = useCallback((event) => {
    setQuickFilterText(event.target.value);
  }, []);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon />
          Analytics Data
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <GridConfigMenu controller={gridConfigs} />
          <Tooltip title="Refresh Data">
            <span>
              <IconButton
                aria-label="Refresh Data"
                onClick={handleRefresh}
                disabled={isLoading}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <span>
              <IconButton
                aria-label="Export to Excel"
                onClick={handleExportToExcel}
                disabled={!rowData.length || !selectedDate}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected
                  }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Current Date Display */}
      {selectedDate && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon fontSize="small" />
            Currently viewing: {selectedDate}
          </Typography>
        </Box>
      )}

      {/* Messages */}
      {message && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={message.severity}>
            {message.text}
          </Alert>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading analytics data...
          </Typography>
        </Box>
      )}

      {/* Quick Filter */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Quick Filter..."
          value={quickFilterText}
          onChange={handleEditFilter}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            sx: {
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }
          }}
        />
      </Box>

      {/* Data Grid */}
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getAnalyticsRowId}
        modules={gridModules}
        height="400px"
        // This component renders its own header, quick filter, export button,
        // footer note, and loading spinner — keep PamGrid's built-in chrome
        // (toolbar/quick-filter/export/footer/loading overlay) off so behavior
        // is unchanged.
        showToolbar={false}
        showQuickFilter={false}
        showExport={false}
        showFooter={false}
        quickFilterText={quickFilterText}
        // Auto-apply default layout once the grid API is live.
        onGridReady={gridConfigs.onGridReady}
        // Mirror filter changes into the local stash; column gestures ride
        // through agGridProps as stashBindings.
        onFilterChanged={gridConfigs.onGridStateChanged}
        // Everything the raw grid passed that isn't a PamGrid named prop is
        // preserved verbatim via agGridProps (overrides PamGrid defaults).
        agGridProps={{
          rowHeight: 32,
          suppressHorizontalScroll: false,
          ...gridConfigs.stashBindings,
        }}
      />

      {/* Footer Note */}
      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> This grid displays comprehensive analytics data including book/market price metrics, yields, spreads, duration, convexity, and risk measures by account and lot.
          Data includes both book price and market price analytics with detailed duration and spread calculations. Use the filters to search for specific CUSIPs, accounts, or lots.
          {selectedDate && (
            <span style={{ display: 'block', marginTop: '4px' }}>
              <strong>Currently viewing:</strong> {selectedDate}
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default AnalyticsGrid;
